/**
 * @file useNotificationInboxes.js
 * @location src/components/layout/notificationbell/useNotificationInboxes.js
 * @role NotificationBell inbox 데이터 — 안 읽음(본인 담당)·중지 통지·미독 독촉(매니저). (검수·멘션·진척 정체 폐기 razor ㉓)
 *
 * 반환:
 *   unack             담당(aee) acknowledgedAt 없는 task (독촉 정렬)
 *   subordinateStalls 매니저 — 팀원의 안 읽음 독촉(수령 안 한 핸드셰이크)
 *   cancelledToMe     담당(aee) 본인 업무 중지 (직전 7일)
 *   total             건수 합계
 */
import { useApp } from '../../../store/AppContext';
import { useMembers } from '../../../store/useMembers';
import { getTodayStr, isClosed } from '../../../data/constants';
import { getVisibleMemberIds } from '../../../utils/visibility';
import { canSeeAllTasks } from '../../../utils/permissions';
import { STALL_THRESHOLDS, stallInfo, assignedSince } from '../../../utils/escalation';
import { upcomingReminders } from '../../../utils/reminders';

export function useNotificationInboxes() {
  const { S } = useApp();
  const { members } = useMembers();

  const me    = members.find(m => m.id === S.uid);
  const isMgr = canSeeAllTasks(me);
  const today = getTodayStr();

  const visibleSet = getVisibleMemberIds(S);
  const allowAee = (aee) => !visibleSet || visibleSet.has(aee);

  const unack = S.tasks.filter(t => t.aee === S.uid && !t.acknowledgedAt && !isClosed(t.status));
  const upcomingDue = upcomingReminders(S.tasks, S.uid, today);

  const cancelledToMe = S.tasks.filter(t => {
    if (t.aee !== S.uid || t.status !== 'cancelled' || !t.cancelledAt) return false;
    const at = Date.parse(t.cancelledAt);
    return Number.isFinite(at) && at >= Date.now() - 7 * 24 * 3600 * 1000;
  });

  const now = new Date();
  const unackS = unack
    .map(t => ({ ...t, _stall: stallInfo(assignedSince(t), STALL_THRESHOLDS.unack, now) }))
    .sort((a, b) => b._stall.days - a._stall.days);

  // 매니저가 보는 팀원 안 읽음 독촉(수령 안 한 핸드셰이크)만 — 진척정체는 razor ㉓ 폐기
  const subordinateStalls = !isMgr ? [] : S.tasks.flatMap(t => {
    if (t.aee === S.uid || !allowAee(t.aee)) return [];
    if (isClosed(t.status) || t.acknowledgedAt) return [];
    const s = stallInfo(assignedSince(t), STALL_THRESHOLDS.unack, now);
    return s.stalled ? [{ ...t, _stall: s, _stage: 'unack' }] : [];
  }).sort((a, b) => b._stall.days - a._stall.days);

  const topStall = [
    ...unackS.map(t => ({ ...t, _src: 'self', _stage: 'unack' })),
    ...subordinateStalls.map(t => ({ ...t, _src: 'sub' })),
  ]
    .filter(t => t._stall?.stalled)
    .sort((a, b) => b._stall.days - a._stall.days)
    .slice(0, 2);

  const topStallKeys = new Set(topStall.map(t => `${t._src}-${t._stage}-${t.id}`));

  const total = unack.length + subordinateStalls.length + cancelledToMe.length + upcomingDue.length;

  return {
    unack: unackS,
    subordinateStalls, cancelledToMe,
    upcomingDue,
    todayKey: today,
    topStall, topStallKeys,
    total,
  };
}
